import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Star, TrendingUp, Users } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'



export default function HeroSection() {
    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
            {/* Background gradient */}


            {/* Animated stars - only visible in dark mode */}


            {/* Animated background circles */}
            <div className="absolute inset-0 overflow-hidden">
                <motion.div
                    className="absolute top-1/4 -left-20 w-96 h-96 bg-primary-blue/20 rounded-full blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute bottom-1/4 -right-20 w-96 h-96 bg-accent-gold/10 rounded-full blur-3xl"
                    animate={{
                        scale: [1.2, 1, 1.2],
                        opacity: [0.2, 0.4, 0.2],
                    }}
                    transition={{ duration: 10, repeat: Infinity }}
                />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left content */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-block mb-6">
                            <span className="px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30">
                                🎯 Transform Your NEET Journey
                            </span>
                        </div>

                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6 leading-tight">
                            <span className="text-theme-primary">Master NEET with</span>
                            <span className="block mt-2 gradient-text text-luxury">
                                Expert Mentorship
                            </span>
                        </h1>

                        <p className="text-lg md:text-xl text-theme-secondary mb-8 max-w-xl">
                            Join India's most comprehensive NEET prep platform with personalized mentorship,
                            advanced test tracking, and proven success strategies.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mb-12">
                            <Link
                                to="/courses"
                                className="group px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2"
                            >
                                Start Your Journey
                                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                                to="/about"
                                className="px-8 py-4 bg-slate-200 dark:bg-slate-800/50 hover:bg-slate-300 dark:hover:bg-slate-800/80 text-theme-primary font-semibold rounded-lg transition-all duration-300 flex items-center justify-center"
                            >
                                Explore Features
                            </Link>
                        </div>

                        {/* Quick stats */}
                        <div className="grid grid-cols-3 gap-6">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="text-center sm:text-left"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Users className="h-5 w-5 text-primary-blue" />
                                    <p className="text-2xl font-bold text-theme-primary">10k+</p>
                                </div>
                                <p className="text-sm text-theme-muted">Students</p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="text-center sm:text-left"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Star className="h-5 w-5 text-accent-gold" />
                                    <p className="text-2xl font-bold text-theme-primary">95%</p>
                                </div>
                                <p className="text-sm text-theme-muted">Success Rate</p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="text-center sm:text-left"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <TrendingUp className="h-5 w-5 text-green-500" />
                                    <p className="text-2xl font-bold text-theme-primary">500+</p>
                                </div>
                                <p className="text-sm text-theme-muted">Mentors</p>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Right content - Floating glassmorphic card */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative hidden lg:block"
                    >
                        <motion.div
                            animate={{
                                y: [0, -20, 0],
                            }}
                            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            <GlassmorphicCard variant="elevated" className="p-8">
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-2xl font-display font-bold text-theme-primary mb-2">
                                            Why Future Founder?
                                        </h3>
                                        <p className="text-theme-secondary text-sm">
                                            Premium features that set you apart
                                        </p>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 rounded-lg bg-primary-blue/20 text-primary-blue">
                                                <Star className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-theme-primary">Expert Mentors</h4>
                                                <p className="text-sm text-theme-muted">One-on-one guidance from NEET toppers</p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-3">
                                            <div className="p-2 rounded-lg bg-accent-gold/20 text-accent-gold">
                                                <TrendingUp className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-theme-primary">Advanced Analytics</h4>
                                                <p className="text-sm text-theme-muted">Track every minute of your preparation</p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-3">
                                            <div className="p-2 rounded-lg bg-green-500/20 text-green-500">
                                                <Users className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-theme-primary">Proven Results</h4>
                                                <p className="text-sm text-theme-muted">95% success rate with our students</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-slate-300 dark:border-slate-700/50">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-theme-muted">Trusted by</span>
                                            <span className="text-2xl font-bold gradient-text">10,000+ Students</span>
                                        </div>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    )
}
